import { ReactElement, useState } from 'react';
import { Duration, Instant } from '@js-joda/core';

import { TimetableEvent } from '@dreryk/react-calendar';
import { hourMinuteFormatter } from '@dreryk/react-common';
import { appendTestableCustomAttribute, appendTestableElementAttributes } from '@dreryk/react-components';

import { ReceptionServiceColorBar } from '~libs.shared.reception-service';
import { protectFeature } from '~libs.shared.system';

import { AppointmentEventSlotData } from '../../AppointmentsTimetable.types';

import {
   StyledAppointmentsTimetableEvent,
   StyledCalendarEventIcons,
   StyledEventTime,
   StyledPatientNameText,
} from './AppointmentsTimetableEvent.styled';
import { getPatientName } from './AppointmentsTimetableEvent.utils';
import { dispatchAppointmentsTimetableEventSelectionEvent } from './AppointmentsTimetableEventSelection.events';
import { AppointmentsTimetableEventAppointmentSourceLabelIcon } from './AppointmentsTimetableEventAppointmentSourceLabelIcon';
import { AppointmentsTimetableEventSettlementStatus } from './AppointmentsTimetableEventSettlementStatus';
import { AppointmentsTimetableEventNotePresenceIcon } from './AppointmentsTimetableEventNotePresenceIcon';
import { AppointmentsTimetableEventVideoConsultationIcon } from './AppointmentsTimetableEventVideoConsultationIcon';
import { AppointmentsTimetableEventUnknownPatientIcon } from './AppointmentsTimetableEventUnknownPatientIcon';
import { AppointmentsTimetableEventAppointmentConfirmationStatusIcon } from './AppointmentsTimetableEventAppointmentConfirmationStatusIcon';
import { AppointmentsTimetableEventPatientPresenceStatusIcon } from './AppointmentsTimetableEventPatientPresenceStatusIcon';

const FIVE_MINUTES = 5;

type AppointmentsTimetableEventProps = TimetableEvent<AppointmentEventSlotData>;

export const AppointmentsTimetableEvent = (props: AppointmentsTimetableEventProps): ReactElement => {
   const [eventElement, setEventElement] = useState<HTMLDivElement | null>(null);

   const handleClick = (): void => {
      dispatchAppointmentsTimetableEventSelectionEvent({
         event: props,
         eventElement: eventElement,
      });
   };

   const duration = Duration.between(
      Instant.ofEpochMilli(props.start.getTime()),
      Instant.ofEpochMilli(props.end.getTime())
   );

   const minimumDuration = duration.toMinutes() <= FIVE_MINUTES;

   return (
      <StyledAppointmentsTimetableEvent
         ref={setEventElement}
         {...appendTestableElementAttributes(`appointmentEvent-${props.data.appointmentId}`)}
         {...appendTestableCustomAttribute('appointmentStatus', props.data.status.toLocaleLowerCase())}
         {...appendTestableCustomAttribute('visitStatus', props.data.visit?.status.toLocaleLowerCase())}
         onClick={handleClick}
         $horizontal={minimumDuration}
      >
         {protectFeature(
            'privateMedicalServicesReadOnly',
            null,
            <ReceptionServiceColorBar
               appointmentCancelled={props.data.status === 'CANCELLED'}
               receptionServices={props.data.receptionServices}
            />
         )}

         <StyledPatientNameText testId={'patientName'} $nowrap={minimumDuration}>
            {getPatientName(props.data)}
         </StyledPatientNameText>
         <StyledEventTime {...appendTestableElementAttributes('time')} $hidden={minimumDuration}>
            {`${hourMinuteFormatter(props.start)} - ${hourMinuteFormatter(props.end)}`}
         </StyledEventTime>
         <StyledCalendarEventIcons $static={minimumDuration}>
            {props.data.timeSlotConfirmed ? (
               <>
                  <AppointmentsTimetableEventAppointmentConfirmationStatusIcon
                     confirmed={props.data.presenceConfirmed}
                  />
                  <AppointmentsTimetableEventPatientPresenceStatusIcon patientPresence={props.data.patientPresence} />
               </>
            ) : null}
            <AppointmentsTimetableEventUnknownPatientIcon
               patientReferenceIndentifier={props.data.patient.referenceIdentifier}
            />
            <AppointmentsTimetableEventVideoConsultationIcon encounterMode={props.data.encounterMode} />
            <AppointmentsTimetableEventNotePresenceIcon notePresent={props.data.notePresent} />
            <AppointmentsTimetableEventAppointmentSourceLabelIcon sourceLabel={props.data.sourceLabel} />
            {protectFeature(
               'privateMedicalServicesReadOnly',
               null,
               <AppointmentsTimetableEventSettlementStatus
                  appointmentId={props.data.appointmentId}
                  visitId={props.data.visit?.id}
               />
            )}
         </StyledCalendarEventIcons>
      </StyledAppointmentsTimetableEvent>
   );
};
